/**
 * Banking Types for Accounting Module
 *
 * Enhanced banking entities including accounts, transactions, and reconciliation.
 */

import type { ISODate, CentsAmount } from '@business-assistant/shared';

/**
 * Bank account type
 */
export type BankAccountType = 'checking' | 'savings' | 'credit' | 'cash';

/**
 * Bank account entity
 */
export interface BankAccount {
  id: string; // bnk-xxx
  accountName: string;
  bankName?: string;
  accountNumber?: string; // Masked for security
  iban?: string;
  swift?: string;
  currency: string; // ISO 4217 code (EUR, USD, etc.)
  accountType: BankAccountType;
  openingBalance: CentsAmount;
  currentBalance: CentsAmount;
  reconciledBalance: CentsAmount;
  lastReconciliationDate?: ISODate;
  isActive: boolean;
  createdAt: ISODate;
  updatedAt: ISODate;
}

/**
 * Bank transaction type
 */
export type BankTransactionType = 'debit' | 'credit';

/**
 * Bank transaction entity
 */
export interface BankTransaction {
  id: string; // btx-xxx
  accountId: string; // bnk-xxx
  transactionDate: ISODate;
  valueDate?: ISODate;
  description: string;
  amount: CentsAmount;
  type: BankTransactionType;
  balance?: CentsAmount; // Balance after transaction
  reference?: string; // Bank reference number
  category?: string; // Auto-suggested category
  matchedInvoiceId?: string; // inv-xxx
  matchedMovementId?: string; // mov-xxx
  reconciliationId?: string; // rcn-xxx
  isReconciled: boolean;
  notes?: string;
  importedFrom?: 'csv' | 'api' | 'manual' | 'ofx' | 'qif' | 'mt940';
  importBatchId?: string;
  createdAt: ISODate;
}

/**
 * Reconciliation status
 */
export type ReconciliationStatus = 'in_progress' | 'completed' | 'requires_review';

/**
 * Reconciliation entity
 */
export interface Reconciliation {
  id: string; // rcn-xxx
  accountId: string; // bnk-xxx
  reconciliationDate: ISODate;
  statementDate: ISODate;
  statementBalance: CentsAmount;
  systemBalance: CentsAmount;
  difference: CentsAmount;
  status: ReconciliationStatus;
  reconciledTransactionIds: string[]; // btx-xxx[]
  unreconciledTransactionIds: string[]; // btx-xxx[]
  adjustments: ReconciliationAdjustment[];
  notes?: string;
  performedBy?: string; // usr-xxx
  approvedBy?: string; // usr-xxx
  createdAt: ISODate;
  completedAt?: ISODate;
}

/**
 * Reconciliation adjustment for discrepancies
 */
export interface ReconciliationAdjustment {
  id: string;
  description: string;
  amount: CentsAmount;
  type: 'bank_fee' | 'interest' | 'correction' | 'other';
  createdAt: ISODate;
}

/**
 * Bank statement import template
 */
export interface BankImportTemplate {
  id: string;
  name: string;
  bankName: string;
  format: 'csv' | 'excel' | 'ofx' | 'qif' | 'mt940';
  columnMappings: {
    dateColumn?: string;
    descriptionColumn?: string;
    amountColumn?: string;
    debitColumn?: string;
    creditColumn?: string;
    balanceColumn?: string;
    referenceColumn?: string;
  };
  dateFormat?: string;
  skipRows?: number;
  delimiter?: string;
  isActive: boolean;
  createdAt: ISODate;
  updatedAt: ISODate;
}

/**
 * Bank statement import result
 */
export interface BankImportResult {
  batchId: string;
  accountId: string;
  fileName: string;
  totalRows: number;
  importedCount: number;
  skippedCount: number;
  duplicateCount: number;
  errorCount: number;
  errors: Array<{ row: number; message: string }>;
  transactions: BankTransaction[];
  createdAt: ISODate;
}

/**
 * Transaction match suggestion
 */
export interface TransactionMatch {
  transactionId: string; // btx-xxx
  matchType: 'invoice' | 'movement';
  matchId: string; // inv-xxx or mov-xxx
  confidence: number; // 0-100
  matchReason: string;
  amountDifference?: CentsAmount;
}

/**
 * Bank account filters
 */
export interface BankAccountFilters {
  accountType?: BankAccountType;
  isActive?: boolean;
  currency?: string;
  search?: string;
}

/**
 * Bank transaction filters
 */
export interface BankTransactionFilters {
  accountId?: string;
  startDate?: ISODate;
  endDate?: ISODate;
  type?: BankTransactionType;
  isReconciled?: boolean;
  isMatched?: boolean;
  category?: string;
  search?: string;
  minAmount?: CentsAmount;
  maxAmount?: CentsAmount;
}

/**
 * Mask IBAN for display (show first 4 and last 4 chars)
 */
export function maskIBAN(iban: string): string {
  if (!iban || iban.length < 8) return iban;
  const cleaned = iban.replace(/\s/g, '');
  return `${cleaned.slice(0, 4)}****${cleaned.slice(-4)}`;
}

/**
 * Mask account number for display
 */
export function maskAccountNumber(accountNumber: string): string {
  if (!accountNumber || accountNumber.length < 4) return accountNumber;
  return `****${accountNumber.slice(-4)}`;
}

/**
 * Calculate match confidence score
 */
export function calculateMatchConfidence(
  transaction: BankTransaction,
  candidate: { amount: CentsAmount; date: ISODate; description?: string }
): number {
  let score = 0;

  // Exact amount match: 50 points
  if (transaction.amount === candidate.amount) {
    score += 50;
  } else {
    // Amount within 1%: 30 points
    const diff = Math.abs(transaction.amount - candidate.amount);
    const tolerance = transaction.amount * 0.01;
    if (diff <= tolerance) {
      score += 30;
    }
  }

  // Date match (within 7 days): 30 points
  const txDate = new Date(transaction.transactionDate);
  const candDate = new Date(candidate.date);
  const daysDiff = Math.abs(txDate.getTime() - candDate.getTime()) / (1000 * 60 * 60 * 24);
  if (daysDiff <= 7) {
    score += 30 - Math.floor(daysDiff * 4); // Closer dates get more points
  }

  // Description match: 20 points
  if (candidate.description && transaction.description) {
    const txDesc = transaction.description.toLowerCase();
    const candDesc = candidate.description.toLowerCase();
    if (txDesc.includes(candDesc) || candDesc.includes(txDesc)) {
      score += 20;
    }
  }

  return Math.min(score, 100);
}
